// src/components/Billing/ContentfulPickPlanPage.js
// @flow
/* eslint-disable react/no-multi-comp */
import React, { useState } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import { log } from '../../utils/jsUtils';

import Testimonial1 from '../../assets/testimonial-eitan-katz.png';
import Testimonial2 from '../../assets/testimonial-ilan-sadon.png';
import Testimonial3 from '../../assets/testimonial-maribeth1.png';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import { Typography, Hidden } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import CheckIcon from '@mui/icons-material/Check';

import { useTheme } from '@mui/material/styles';

// Instead of `makeStyles(styles)`, we now import our style definitions:
import contentfulPricingSectionStyles from './ContentfulPricingSection.Style';

type PlanType = {
  id: string,
  title: string,
  ctaLabel: string,
  subTitle?: string,
  monthly?: number | string,
  yearly?: number | string,
  price?: string, // used if !enableMonthYearSwitch
  linkMonthly?: string,
  linkYearly?: string,
  link?: string, // used if !enableMonthYearSwitch
  planId?: string,
  monthlyPlanId?: string,
  yearlyPlanId?: string,
  monthlyTestPlanId?: string,
  yearlyTestPlanId?: string
};

type FeatureType = {
  label: string,
  plans: Array<string>,
  isHeader?: boolean
};

type MobilePlanFeature = {
  label: string,
  description: string
};

type MobilePlanType = {
  planId: string,
  features: Array<MobilePlanFeature>
};

type PricingTableType = {
  enableMonthYearSwitch: boolean,
  showOnlyMobilePlans: boolean,
  plans: Array<PlanType>,
  features: Array<FeatureType>,
  plansMobile: Array<MobilePlanType>
};

type Props = {
  selectPlan: (
    planId: string,
    isPeriodYearly: boolean,
    planDef: PlanType,
    planIndex: number,
    plan: PlanType,
    pricingTable: PricingTableType
  ) => void,
  pricingTable: PricingTableType
};

const ContentfulPickPlanPage = (props: Props) => {
  const { pricingTable, selectPlan } = props;
  log('[ContentfulPickPlanPage] pricingTable => ', pricingTable);

  // Pull in MUI theme
  const theme = useTheme();

  // Instead of `makeStyles`, we build style references:
  const classes = {
    container: contentfulPricingSectionStyles.container(theme),
    yearlyToggle: contentfulPricingSectionStyles.yearlyToggle(theme),
    gridPaper: contentfulPricingSectionStyles.gridPaper(theme),
    featuresList: contentfulPricingSectionStyles.featuresList(theme),
    featureHeader: contentfulPricingSectionStyles.featureHeader(theme),
    gridCell: contentfulPricingSectionStyles.gridCell(theme),
    featureDesc: contentfulPricingSectionStyles.featureDesc(theme),
    emphasisDescr: contentfulPricingSectionStyles.emphasisDescr(theme),
    planTitle: contentfulPricingSectionStyles.planTitle(theme),
    planPrice: contentfulPricingSectionStyles.planPrice(theme),
    planPricePAYG: contentfulPricingSectionStyles.planPricePAYG(theme),
    striked: contentfulPricingSectionStyles.striked(theme),
    hiddenSpace: contentfulPricingSectionStyles.hiddenSpace(theme),
    checkIcon: contentfulPricingSectionStyles.checkIcon(theme),
    topBuy: contentfulPricingSectionStyles.topBuy(theme),
    featureItem: contentfulPricingSectionStyles.featureItem(theme),
    emphasis: contentfulPricingSectionStyles.emphasis(theme),
    mobileOnlyPricingGridContainer:
      contentfulPricingSectionStyles.mobileOnlyPricingGridContainer(theme),
    mobileOnlyGridPaper: contentfulPricingSectionStyles.mobileOnlyGridPaper(
      theme,
      { plansNb: pricingTable.plans.length }
    )
  };

  const [isPeriodYearly, setIsPeriodYearly] = useState(true);

  return (
    <>
      {/* Optional testimonials */}
      <img src={Testimonial1} alt="Testimonial1" width="350" />
      <img src={Testimonial2} alt="Testimonial2" width="350" />
      <img src={Testimonial3} alt="Testimonial3" width="350" />

      {pricingTable ? (
        <Grid
          container
          direction="column"
          style={classes.container}
        >
          {/* Switch monthly/yearly if enabled */}
          {pricingTable.enableMonthYearSwitch && (
            <Grid item xs={12} style={classes.yearlyToggle}>
              <Grid
                alignItems="center"
                component="label"
                container
                spacing={1}
              >
                <Grid item>{'Billed Monthly'}</Grid>
                <Grid item>
                  <Switch
                    checked={isPeriodYearly}
                    color="primary"
                    name="isPeriodYearly"
                    onChange={(e) => setIsPeriodYearly(e.target.checked)}
                  />
                </Grid>
                <Grid item>{'Billed Yearly'}</Grid>
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            {/* Desktop layout if not showOnlyMobilePlans */}
            {!pricingTable.showOnlyMobilePlans && (
              <Paper style={classes.gridPaper} elevation={4}>
                <Hidden only={['xs']}>
                  <Grid container direction="row">
                    {/* left column with features */}
                    <Grid
                      item
                      xs={
                        12 -
                        pricingTable.plans.length *
                          Math.floor(9 / pricingTable.plans.length)
                      }
                    >
                      <Grid container style={classes.featuresList}>
                        {/* blank cell */}
                        <Grid style={classes.featureHeader} item />
                        {pricingTable.features.map((feature, featureIndex) => (
                          <Grid
                            style={{
                              ...classes.gridCell,
                              ...classes.featureDesc,
                              ...(feature.isHeader ? classes.emphasisDescr : {}),
                              ...(pricingTable.features.length === featureIndex + 1
                                ? { borderBottom: 0 }
                                : {})
                            }}
                            item
                            key={`feature-${featureIndex}`}
                          >
                            {feature.label}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* plan columns */}
                    {pricingTable.plans.map((plan, planIndex) => {
                      // find planDef if needed:
                      const planDef = R.find(R.propEq('id', plan.id))(
                        pricingTable.plans
                      );

                      return (
                        <Grid
                          item
                          key={`plan-${planIndex}`}
                          xs={Math.floor(9 / pricingTable.plans.length)}
                        >
                          <Grid container style={classes.featuresList}>
                            {/* plan header */}
                            <Grid style={classes.featureHeader} item>
                              <Typography style={classes.planTitle} variant="h2">
                                {plan.title}
                              </Typography>
                              {/* Pricing logic (monthly/yearly) */}
                              {!pricingTable.enableMonthYearSwitch ? (
                                <Typography
                                  style={{
                                    ...classes.planPrice,
                                    ...(plan.yearly === -1 ? classes.planPricePAYG : {})
                                  }}
                                  variant="h3"
                                >
                                  {plan.yearly === -1 ? (
                                    <span>
                                      Contact us
                                      <br />
                                      +1 (917) 451 5515
                                    </span>
                                  ) : (
                                    `$${plan.yearly}`
                                  )}
                                </Typography>
                              ) : isPeriodYearly ? (
                                <div className="discountedPrice">
                                  <Typography
                                    style={{
                                      ...classes.planPrice,
                                      ...(plan.yearly === -1 ? classes.planPricePAYG : {})
                                    }}
                                    variant="h3"
                                  >
                                    {plan.yearly === -1 ? (
                                      <span>
                                        Contact us
                                        <br />
                                        +1 (917) 451 5515
                                      </span>
                                    ) : (
                                      `$${plan.yearly}/mo`
                                    )}
                                  </Typography>
                                  {plan.monthly !== -1 ? (
                                    <Typography style={classes.striked} variant="h4">
                                      {`$${plan.monthly}`}/mo
                                    </Typography>
                                  ) : (
                                    <Typography style={classes.striked} variant="h4">
                                      &nbsp;
                                    </Typography>
                                  )}
                                </div>
                              ) : (
                                <div className="discountedPrice">
                                  <Typography
                                    style={{
                                      ...classes.planPrice,
                                      ...(plan.monthly === -1
                                        ? classes.planPricePAYG
                                        : {})
                                    }}
                                    variant="h3"
                                  >
                                    {plan.monthly === -1 ? (
                                      <span>
                                        Contact us
                                        <br />
                                        +1 (917) 451 5515
                                      </span>
                                    ) : (
                                      `$${plan.monthly}/mo`
                                    )}
                                  </Typography>
                                  <Typography style={classes.hiddenSpace} variant="h4">
                                    &nbsp;
                                  </Typography>
                                </div>
                              )}
                              {/* The "Select" button if plan is not -1 */}
                              {plan.yearly !== -1 && (
                                <Button
                                  style={classes.topBuy}
                                  color="primary"
                                  variant="contained"
                                  onClick={() =>
                                    selectPlan(
                                      isPeriodYearly
                                        ? process.env.REACT_APP_ENVF === 'env.production'
                                          ? planDef.yearlyPlanId
                                          : planDef.yearlyTestPlanId
                                        : process.env.REACT_APP_ENVF === 'env.production'
                                        ? planDef.monthlyPlanId
                                        : planDef.monthlyTestPlanId,
                                      isPeriodYearly,
                                      planDef,
                                      planIndex,
                                      plan,
                                      pricingTable
                                    )
                                  }
                                >
                                  Select
                                </Button>
                              )}
                            </Grid>

                            {/* plan features */}
                            {pricingTable.features.map((feature, featureIndex2) => (
                              <Grid
                                style={{
                                  ...classes.gridCell,
                                  ...classes.featureItem,
                                  ...(feature.isHeader ? classes.emphasis : {}),
                                  ...(pricingTable.features.length === featureIndex2 + 1
                                    ? { borderBottom: 0 }
                                    : {})
                                }}
                                item
                                key={`feature-plan${planIndex}-${featureIndex2}`}
                              >
                                {feature.plans?.[planIndex] === 'checked' ? (
                                  <FontAwesomeIcon
                                    style={classes.checkIcon}
                                    icon={faCheck}
                                  />
                                ) : (
                                  <pre>{feature.plans?.[planIndex]}</pre>
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Hidden>

                {/* Mobile view */}
                <Hidden only={['sm', 'md', 'lg', 'xl']}>
                  {pricingTable.plansMobile?.map((mobilePlan, planIndex2) => {
                    const planDef = R.find(R.propEq('id', mobilePlan.planId))(
                      pricingTable.plans
                    );
                    return (
                      <div key={planIndex2}>
                        {planDef && (
                          <Grid
                            container
                            style={classes.featuresList}
                            key={`mobile-plan-${planIndex2}`}
                          >
                            <Grid style={classes.featureHeader} item>
                              <Typography variant="h2">{planDef.title}</Typography>
                              {isPeriodYearly ? (
                                <div className="discountedPrice">
                                  <Typography variant="h3">{`$${planDef.yearly}`}/mo</Typography>
                                  <Typography style={classes.striked} variant="h4">
                                    {`$${planDef.monthly}`}/mo
                                  </Typography>
                                </div>
                              ) : (
                                <div>
                                  <Typography variant="h3">{`$${planDef.monthly}`}/mo</Typography>
                                  <Typography style={classes.hiddenSpace} variant="h4">
                                    -
                                  </Typography>
                                </div>
                              )}
                            </Grid>
                            {mobilePlan.features.map((feature, featureIndex3) => (
                              <Grid
                                style={{
                                  ...classes.gridCell,
                                  ...(featureIndex3 === 0
                                    ? classes.featureDesc
                                    : classes.featureDesc),
                                  ...(mobilePlan.features.length === featureIndex3 + 1
                                    ? { borderBottom: 0 }
                                    : {})
                                }}
                                item
                                key={`mobile-plan${planIndex2}-feature${featureIndex3}`}
                              >
                                {feature.label}
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </div>
                    );
                  })}
                </Hidden>
              </Paper>
            )}

            {/* If showOnlyMobilePlans is true => new mobile layout */}
            {pricingTable.showOnlyMobilePlans && (
              <div style={classes.mobileOnlyPricingGridContainer}>
                {pricingTable.plansMobile?.map((mobilePlan, planIndex3) => {
                  const planDef = R.find(R.propEq('id', mobilePlan.planId))(
                    pricingTable.plans
                  );
                  return (
                    <Paper
                      style={classes.mobileOnlyGridPaper}
                      elevation={4}
                      key={planIndex3}
                    >
                      {planDef && (
                        <Grid container style={classes.featuresList}>
                          <Grid style={classes.featureHeader} item>
                            <Typography variant="h2">{planDef.title}</Typography>
                            {isPeriodYearly ? (
                              <div className="discountedPrice">
                                <Typography variant="h3">{`$${planDef.yearly}`}/mo</Typography>
                                <Typography style={classes.striked} variant="h4">
                                  {`$${planDef.monthly}`}/mo
                                </Typography>
                              </div>
                            ) : (
                              <div>
                                <Typography variant="h3">{`$${planDef.monthly}`}/mo</Typography>
                                <Typography style={classes.hiddenSpace} variant="h4">
                                  -
                                </Typography>
                              </div>
                            )}
                            {/* Select plan button if not -1 */}
                            {planDef.yearly !== -1 && (
                              <Button
                                style={classes.topBuy}
                                color="primary"
                                variant="contained"
                                onClick={() =>
                                  selectPlan(
                                    isPeriodYearly
                                      ? process.env.REACT_APP_ENVF === 'env.production'
                                        ? planDef.yearlyPlanId
                                        : planDef.yearlyTestPlanId
                                      : process.env.REACT_APP_ENVF === 'env.production'
                                      ? planDef.monthlyPlanId
                                      : planDef.monthlyTestPlanId,
                                    isPeriodYearly,
                                    planDef,
                                    planIndex3,
                                    planDef,
                                    pricingTable
                                  )
                                }
                              >
                                Select
                              </Button>
                            )}
                          </Grid>

                          {mobilePlan.features.map((feature, featureIndex4) => (
                            <Grid
                              style={{
                                ...classes.gridCell,
                                ...(featureIndex4 === 0 ? classes.featureDesc : {}),
                                ...(mobilePlan.features.length === featureIndex4 + 1
                                  ? { borderBottom: 0 }
                                  : {})
                              }}
                              item
                              key={`mobileonly-plan${planIndex3}-feature${featureIndex4}`}
                            >
                              {feature.label}
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Paper>
                  );
                })}
              </div>
            )}
          </Grid>
        </Grid>
      ) : null}
    </>
  );
};

export default ContentfulPickPlanPage;